// The Market Probabilities board market set + per-market identity helpers, factored
// out of the provider so the reducer, the two effect hooks, and the projection
// all share one definition. No React, no WS.
import { peekAvailableTokens } from '../exotics-tokens/use-available-tokens';
import {
  BTC_STEP,
  resolveMarketBaseQuote,
  stepFor,
  type ResolvedMarket,
} from '../../pages/market-probabilities/ladder';
import type { ModelKind } from '../../pages/exotics-pricer/contracts';

// A board market. `token` is the display + registry baseCcy key. The existing
// four pin `baseQuote` + `ccy` explicitly (an override) so their exact wire
// never regresses; the new four omit both and are REGISTRY-RESOLVED (their
// quote convention -- *_USDC vs *_USDT -- is read off the live surface registry
// rather than guessed). A market that resolves neither an override nor a
// registry entry lands uncovered.
export interface MarketConfig {
  token: string;
  baseQuote?: string;
  ccy?: string;
  step?: number;
}

export const MARKETS: MarketConfig[] = [
  // The six tokens the market_probabilities feed serves (venue deribit, kind
  // one_touch). BTC + ETH pin their wire explicitly (OVERRIDE) so their exact
  // quote never regresses. SOL / XRP / AVAX / TRX are REGISTRY-RESOLVED (token ==
  // baseCcy, no override): their quote convention (*_USDC vs *_USDT) and candle
  // venue ride off the live vol-surface registry rather than a hard-coded guess
  // -- see resolveMarketBaseQuote in ladder.ts. A token whose surface the registry
  // doesn't carry lands uncovered ("No vol surface"); its feed data still arrives
  // but the anchor/MTD context is what gates the card. Per-card `decimals` are
  // derived from spot magnitude (decimalsForPrice), so the sub-$1 TRX and the
  // tens-of-dollars AVAX render at the right precision with no per-token step.
  { token: 'BTC', baseQuote: 'BTC', ccy: 'USD', step: BTC_STEP },
  { token: 'ETH', baseQuote: 'ETH', ccy: 'USD' },
  { token: 'SOL' },
  { token: 'XRP' },
  { token: 'AVAX' },
  { token: 'TRX' },
  // HYPE intentionally EXCLUDED: it does not price -- the backend returns a
  // -32603 internal error (vol-surface gate, not an FE bug). Re-add `{ token:
  // 'HYPE' }` here once the backend surface / -32603 fix lands.
];

// Each un-triggered rung is priced under both models. BS is the faded
// reference column; LV is the headline. Live spot rides back on whichever
// response lands first (both carry market.spot).
export const MODELS: ModelKind[] = ['bs', 'lv'];

// Ladder DEPTH is band-derived, not a fixed count: rungCountForBand sizes the
// rungs-per-side to span ~+/-30% of each market's start-of-month anchor, capped
// at 12 per side to bound the fan-out (see ladder.ts). BTC (72k anchor, 2500
// step) -> 9 rungs down, reaching ~$50k; other markets get a comparable % span
// off their own step. The full level set is the product; we do NOT shrink it to
// fit the server. All levels render immediately from the anchor; prices stream
// in progressively (near-money first). Full/instant population across the deep
// tail + all expiries is a THROUGHPUT problem solved by the batch/streaming
// endpoint, not by cutting levels.
// Static fallback band used before the ATMF vol is known (first pricing cycle).
// 50% / 20 gives BTC monthly floor ~55k from a 104k open (covers a 47% drop)
// and year-end floor ~47k from a 93.5k SOY open (covers a 50% drop).
export const BAND_PCT = 0.5;
export const RUNG_CAP = 20;

// Delta outer boundary for vol-implied rung count. 2Δ ≈ 4% touch probability
// under zero-drift BS (touch_prob ≈ 2×delta). Rebuild triggers on every
// SET_SMILE for the current-month and December expiries. DELTA_RUNG_CAP bounds
// the count (≤25 rungs per side keeps pricing tractable before batch landing).
export const DELTA_BOUND = 0.02;
export const DELTA_RUNG_CAP = 25;

// The override an existing market pins, or undefined for a registry-resolved one.
export const overrideFor = (cfg: MarketConfig): { baseQuote: string; ccy: string } | undefined =>
  cfg.baseQuote !== undefined && cfg.ccy !== undefined
    ? { baseQuote: cfg.baseQuote, ccy: cfg.ccy }
    : undefined;

export const stepForMarket = (cfg: MarketConfig, somSpot: number): number =>
  cfg.step ?? stepFor(somSpot);

// Synchronous seed for the per-market resolved identity (baseQuote / ccy /
// candlestick source). Reads the cached vol-surface registry snapshot
// (peekAvailableTokens) -- populated if any component already fetched the
// registry this session -- and resolves each MARKET up front, so the very
// FIRST MTD fetch + fan-out already carry the correct baseQuote and venue
// (HYPE -> HYPE_USDT / MEXC, the registry-resolved four -> their real wire) on
// a warm cache. On a cold cache the snapshot is []: the OVERRIDE markets still
// resolve (their baseQuote is pinned, source defaults DERIBIT) and the
// REGISTRY markets resolve to null (uncovered) until the async coverage effect
// lands and refreshes the ref.
export const seedResolvedByToken = (): Record<string, ResolvedMarket | null> => {
  const snapshot = peekAvailableTokens();
  const resolved: Record<string, ResolvedMarket | null> = {};
  for (const m of MARKETS) {
    resolved[m.token] = resolveMarketBaseQuote(snapshot, m.token, overrideFor(m));
  }
  return resolved;
};
